import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { AuthService } from './auth.service';

const PURCHASE_PENDING_WINDOW_MS = 3 * 60 * 1000;
const PENDING_PRODUCT_KEY_STORAGE_KEY = 'propdesk_pending_purchase_product';

// Stripe's Payment Link "after payment" redirect only fires on a genuinely
// completed payment — it lands the buyer back on /downloads with
// ?session_id={CHECKOUT_SESSION_ID}. That's the only trustworthy signal that
// a purchase actually succeeded (there is no other channel back to the app;
// the buyer may have paid in a different tab than the one they clicked Buy
// in). The product key is stashed in localStorage at Buy-click time, since a
// fresh tab from Stripe's redirect has no in-memory state from the tab that
// initiated checkout.
@Injectable({ providedIn: 'root' })
export class CheckoutReturnService {
  private readonly purchasePendingSubject = new BehaviorSubject(false);
  private readonly pendingProductKeySubject = new BehaviorSubject('');

  private purchasePendingTimeout?: ReturnType<typeof setTimeout>;

  // Not signed in — nothing will unlock on its own; they have to sign in
  // (with the same email used at checkout) to claim the purchase.
  readonly showSignInToClaim$: Observable<boolean>;

  // Signed in — the webhook/claim + live listener will unlock this
  // automatically, no action needed.
  readonly showPurchasePendingBanner$: Observable<boolean>;

  constructor(private auth: AuthService) {
    // Each product uses its own access check rather than a Level-Map-Pro–specific
    // one, which would hide the banner for someone who already owns Level Map Pro
    // but just bought a different product.
    const pendingProductAccessGranted$: Observable<boolean> = combineLatest([
      this.pendingProductKeySubject,
      this.auth.allowedTools$,
    ]).pipe(
      map(([productKey]) => {
        if (!productKey || productKey === 'level-map-pro') return this.auth.canAccessLevelData;
        return this.auth.isToolAllowed(productKey);
      })
    );

    this.showSignInToClaim$ = combineLatest([
      this.purchasePendingSubject,
      pendingProductAccessGranted$,
      this.auth.user$,
    ]).pipe(map(([pending, granted, user]) => pending && !granted && !user));

    this.showPurchasePendingBanner$ = combineLatest([
      this.purchasePendingSubject,
      pendingProductAccessGranted$,
      this.auth.user$,
    ]).pipe(map(([pending, granted, user]) => pending && !granted && !!user));
  }

  // Call right before opening Stripe checkout, so the redirect back can be
  // matched to a product even in a fresh tab.
  recordPendingPurchase(productKey = ''): void {
    try {
      localStorage.setItem(PENDING_PRODUCT_KEY_STORAGE_KEY, productKey);
    } catch {
      // Storage unavailable (private browsing, etc.) — banner just won't know
      // the specific product; pendingProductAccessGranted$ falls back sanely.
    }
  }

  // Call on /downloads load with the `session_id` query param value (or null).
  // Only a non-null session_id — i.e. Stripe's own completed-payment redirect
  // — starts the pending window.
  checkForCompletedCheckout(sessionId: string | null): void {
    if (!sessionId) return;

    // Deferred rather than applied synchronously: this is called from a
    // routed component's ngOnInit, which runs mid-way through the change
    // detection pass that's currently rendering it — by that point
    // AppComponent's own bindings (the global banner, elsewhere in the tree)
    // have already been checked for this pass. Mutating the subjects here
    // directly causes Angular's dev-mode double-check to throw
    // ExpressionChangedAfterItHasBeenCheckedError, and that thrown error
    // corrupts the render, leaving the banner invisible until some unrelated
    // event (e.g. a scroll) triggers a fresh, clean pass. Pushing the actual
    // mutation to a new task means it always starts its own clean pass.
    setTimeout(() => {
      let productKey = '';
      try {
        productKey = localStorage.getItem(PENDING_PRODUCT_KEY_STORAGE_KEY) ?? '';
        localStorage.removeItem(PENDING_PRODUCT_KEY_STORAGE_KEY);
      } catch {
        // Ignore — proceed with an empty product key.
      }

      this.pendingProductKeySubject.next(productKey);
      this.purchasePendingSubject.next(true);
      clearTimeout(this.purchasePendingTimeout);
      this.purchasePendingTimeout = setTimeout(() => this.purchasePendingSubject.next(false), PURCHASE_PENDING_WINDOW_MS);
    }, 0);
  }

  dismiss(): void {
    clearTimeout(this.purchasePendingTimeout);
    this.purchasePendingSubject.next(false);
  }

  async signInToClaimPurchase(): Promise<void> {
    try {
      await this.auth.signInWithGoogle();
    } catch {
      // Popup closed or blocked — let them retry from the same banner.
    }
  }
}
